import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { Plus, Pencil, Trash2, Search, User as UserIcon, ChevronDown, ChevronUp, Upload, File, X } from 'lucide-react';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Textarea } from '../components/ui/textarea';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from '../components/ui/dialog';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { toast } from 'sonner';
import { getErrorMessage } from '../utils/errorHandler';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const Evolucao = () => {
  const [anamneses, setAnamneses] = useState([]);
  const [contacts, setContacts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [editingAnamnese, setEditingAnamnese] = useState(null);
  const [searchTerm, setSearchTerm] = useState('');
  
  // Collapsed sections state
  const [collapsedSections, setCollapsedSections] = useState({
    queixa: true,
    saude: true,
    pessoal: true,
    habitos: true,
    profissional: true,
  });

  const [formData, setFormData] = useState({
    nome_completo: '',
    data_nascimento: '',
    nivel_suporte: '',
    genero: '',
    endereco_completo: '',
    telefone_principal: '',
    contact_id_principal: '',
    telefone_secundario: '',
    contact_id_secundario: '',
    email: '',
    profissao: '',
    escolaridade: '',
    estado_civil: '',
    motivo_busca: '',
    duracao_sintomas: '',
    frequencia_sintomas: '',
    tentativas_anteriores: '',
    expectativas_terapia: '',
    doencas_fisicas: '',
    medicamentos_atuais: '',
    alergias: '',
    com_quem_reside: '',
    dinamica_familiar: '',
    qualidade_sono: '',
    detalhes_sono: '',
    alimentacao: '',
    lazer_hobbies: '',
    satisfacao_trabalho: '',
    impressoes_iniciais: '',
    plano_acompanhamento: '',
    observacoes_adicionais: [],
    documentos: [],
  });
  
  const [novaObservacao, setNovaObservacao] = useState('');
  const [uploadingDoc, setUploadingDoc] = useState(false);

  useEffect(() => {
    fetchAnamneses();
    fetchContacts();
  }, []);

  const fetchAnamneses = async () => {
    try {
      const response = await axios.get(`${API}/anamneses`);
      setAnamneses(response.data);
    } catch (error) {
      toast.error('Erro ao carregar anamneses');
    }
    setLoading(false);
  };

  const fetchContacts = async () => {
    try {
      const response = await axios.get(`${API}/contacts`, {
        params: { per_page: 5000 }
      });
      setContacts(response.data.contacts || []);
    } catch (error) {
      console.error('Erro ao carregar contatos');
    }
  };

  const handlePhoneChange = (phone, field) => {
    setFormData({ ...formData, [field]: phone });
    
    const matchingContact = contacts.find(c => c.phone === phone);
    if (matchingContact) {
      const contactField = field === 'telefone_principal' ? 'contact_id_principal' : 'contact_id_secundario';
      setFormData(prev => ({ ...prev, [contactField]: matchingContact.id }));
      toast.success(`Contato vinculado: ${matchingContact.name}`);
    }
  };
  
  const handleAddObservacao = async () => {
    if (!novaObservacao.trim()) return;
    
    if (editingAnamnese) {
      try {
        await axios.post(`${API}/anamneses/${editingAnamnese.id}/observacao`, null, {
          params: { texto: novaObservacao }
        });
        toast.success('Observação adicionada!');
        setNovaObservacao('');
        
        // Refresh to get updated data
        const response = await axios.get(`${API}/anamneses/${editingAnamnese.id}`);
        setFormData(response.data);
      } catch (error) {
        toast.error('Erro ao adicionar observação');
      }
    } else {
      // Add locally for new anamnese
      const obs = {
        id: Date.now().toString(),
        texto: novaObservacao,
        data: new Date().toISOString()
      };
      setFormData(prev => ({
        ...prev,
        observacoes_adicionais: [...prev.observacoes_adicionais, obs]
      }));
      setNovaObservacao('');
    }
  };
  
  const handleUploadDoc = async (e) => {
    const file = e.target.files[0];
    if (!file) return;
    
    if (editingAnamnese) {
      setUploadingDoc(true);
      try {
        const formData = new FormData();
        formData.append('file', file);
        
        const response = await axios.post(
          `${API}/anamneses/${editingAnamnese.id}/documento`,
          formData,
          { headers: { 'Content-Type': 'multipart/form-data' } }
        );
        
        toast.success('Documento anexado!');
        
        // Refresh data
        const anamneseResponse = await axios.get(`${API}/anamneses/${editingAnamnese.id}`);
        setFormData(anamneseResponse.data);
      } catch (error) {
        toast.error('Erro ao anexar documento');
      }
      setUploadingDoc(false);
    } else {
      toast.warning('Salve a anamnese primeiro antes de anexar documentos');
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    try {
      if (editingAnamnese) {
        await axios.put(`${API}/anamneses/${editingAnamnese.id}`, formData);
        toast.success('Anamnese atualizada!');
      } else {
        await axios.post(`${API}/anamneses`, formData);
        toast.success('Anamnese criada!');
      }
      setDialogOpen(false);
      resetForm();
      fetchAnamneses();
    } catch (error) {
      const errorMessage = getErrorMessage(error);
      toast.error(errorMessage || 'Erro ao salvar anamnese');
    }
  };

  const handleDelete = async (id) => {
    if (!window.confirm('Tem certeza que deseja excluir esta anamnese?')) return;
    
    try {
      await axios.delete(`${API}/anamneses/${id}`);
      toast.success('Anamnese excluída!');
      fetchAnamneses();
    } catch (error) {
      toast.error('Erro ao excluir anamnese');
    }
  };

  const resetForm = () => {
    setFormData({
      nome_completo: '', data_nascimento: '', nivel_suporte: '', genero: '',
      endereco_completo: '', telefone_principal: '', contact_id_principal: '',
      telefone_secundario: '', contact_id_secundario: '', email: '',
      profissao: '', escolaridade: '', estado_civil: '', motivo_busca: '',
      duracao_sintomas: '', frequencia_sintomas: '', tentativas_anteriores: '',
      expectativas_terapia: '', doencas_fisicas: '', medicamentos_atuais: '',
      alergias: '', com_quem_reside: '', dinamica_familiar: '',
      qualidade_sono: '', detalhes_sono: '',
      alimentacao: '', lazer_hobbies: '',
      satisfacao_trabalho: '', impressoes_iniciais: '',
      plano_acompanhamento: '', observacoes_adicionais: [], documentos: [],
    });
    setEditingAnamnese(null);
    setCollapsedSections({
      queixa: true,
      saude: true,
      pessoal: true,
      habitos: true,
      profissional: true,
    });
  };

  const openEditDialog = (anamnese) => {
    setEditingAnamnese(anamnese);
    setFormData(anamnese);
    setDialogOpen(true);
  };
  
  const toggleSection = (section) => {
    setCollapsedSections(prev => ({
      ...prev,
      [section]: !prev[section]
    }));
  };

  const filteredAnamneses = anamneses.filter(a =>
    a.nome_completo.toLowerCase().includes(searchTerm.toLowerCase()) ||
    a.email.toLowerCase().includes(searchTerm.toLowerCase())
  );

  if (loading) {
    return (
      <div className="flex justify-center items-center h-64">
        <div className="animate-spin rounded-full h-12 w-12 border-t-2 border-b-2 border-emerald-700"></div>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      <div className="flex justify-between items-center">
        <div>
          <h1 className="text-3xl font-bold text-gray-900">Evolução</h1>
          <p className="text-gray-600">Anamnese Psicológica</p>
        </div>
        
        <Dialog open={dialogOpen} onOpenChange={(open) => {
          setDialogOpen(open);
          if (!open) resetForm();
        }}>
          <DialogTrigger asChild>
            <Button className="bg-emerald-700 hover:bg-emerald-800 text-white">
              <Plus className="w-4 h-4 mr-2" />
              Nova Anamnese
            </Button>
          </DialogTrigger>
          <DialogContent className="max-w-4xl max-h-[90vh] overflow-y-auto">
            <DialogHeader>
              <DialogTitle>
                {editingAnamnese ? 'Editar Anamnese' : 'Nova Anamnese'}
              </DialogTitle>
            </DialogHeader>
            
            <form onSubmit={handleSubmit} className="space-y-6">
              {/* DADOS DE IDENTIFICAÇÃO - NÃO COLAPSÁVEL */}
              <div className="space-y-4 border-2 border-emerald-200 rounded-lg p-4">
                <h3 className="text-lg font-semibold text-emerald-700 border-b pb-2">
                  Dados de Identificação *
                </h3>
                
                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <Label>Nome Completo *</Label>
                    <Input
                      value={formData.nome_completo}
                      onChange={(e) => setFormData({ ...formData, nome_completo: e.target.value })}
                      required
                    />
                  </div>
                  
                  <div>
                    <Label>Data de Nascimento *</Label>
                    <Input
                      type="date"
                      value={formData.data_nascimento}
                      onChange={(e) => setFormData({ ...formData, data_nascimento: e.target.value })}
                      required
                    />
                  </div>
                  
                  <div>
                    <Label>Nível de Suporte</Label>
                    <Select
                      value={formData.nivel_suporte}
                      onValueChange={(value) => setFormData({ ...formData, nivel_suporte: value })}
                    >
                      <SelectTrigger>
                        <SelectValue placeholder="Selecione" />
                      </SelectTrigger>
                      <SelectContent position="popper">
                        <SelectItem value="suporte 1">Suporte 1</SelectItem>
                        <SelectItem value="suporte 2">Suporte 2</SelectItem>
                        <SelectItem value="suporte 3">Suporte 3</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>
                  
                  <div>
                    <Label>Gênero *</Label>
                    <Select
                      value={formData.genero}
                      onValueChange={(value) => setFormData({ ...formData, genero: value })}
                      required
                    >
                      <SelectTrigger>
                        <SelectValue placeholder="Selecione" />
                      </SelectTrigger>
                      <SelectContent position="popper">
                        <SelectItem value="Feminino">Feminino</SelectItem>
                        <SelectItem value="Masculino">Masculino</SelectItem>
                        <SelectItem value="Não Binário">Não Binário</SelectItem>
                        <SelectItem value="Outro">Outro</SelectItem>
                        <SelectItem value="Prefiro não informar">Prefiro não informar</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>
                </div>
                
                <div>
                  <Label>Endereço Completo *</Label>
                  <Textarea
                    value={formData.endereco_completo}
                    onChange={(e) => setFormData({ ...formData, endereco_completo: e.target.value })}
                    required
                    rows={2}
                  />
                </div>
                
                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <Label>Telefone Principal *</Label>
                    <Input
                      value={formData.telefone_principal}
                      onChange={(e) => handlePhoneChange(e.target.value, 'telefone_principal')}
                      required
                      placeholder="+5511999887766"
                    />
                  </div>
                  
                  <div>
                    <Label>Telefone Secundário</Label>
                    <Input
                      value={formData.telefone_secundario}
                      onChange={(e) => handlePhoneChange(e.target.value, 'telefone_secundario')}
                      placeholder="+5511988776655"
                    />
                  </div>
                  
                  <div>
                    <Label>E-mail *</Label>
                    <Input
                      type="email"
                      value={formData.email}
                      onChange={(e) => setFormData({ ...formData, email: e.target.value })}
                      required
                    />
                  </div>
                  
                  <div>
                    <Label>Profissão/Ocupação *</Label>
                    <Input
                      value={formData.profissao}
                      onChange={(e) => setFormData({ ...formData, profissao: e.target.value })}
                      required
                    />
                  </div>
                  
                  <div>
                    <Label>Escolaridade *</Label>
                    <Select
                      value={formData.escolaridade}
                      onValueChange={(value) => setFormData({ ...formData, escolaridade: value })}
                      required
                    >
                      <SelectTrigger>
                        <SelectValue placeholder="Selecione" />
                      </SelectTrigger>
                      <SelectContent position="popper">
                        <SelectItem value="Ensino Fundamental Incompleto">Ensino Fundamental Incompleto</SelectItem>
                        <SelectItem value="Ensino Fundamental Completo">Ensino Fundamental Completo</SelectItem>
                        <SelectItem value="Ensino Médio Incompleto">Ensino Médio Incompleto</SelectItem>
                        <SelectItem value="Ensino Médio Completo">Ensino Médio Completo</SelectItem>
                        <SelectItem value="Ensino Superior Incompleto">Ensino Superior Incompleto</SelectItem>
                        <SelectItem value="Ensino Superior Completo">Ensino Superior Completo</SelectItem>
                        <SelectItem value="Pós-graduação">Pós-graduação</SelectItem>
                        <SelectItem value="Mestrado">Mestrado</SelectItem>
                        <SelectItem value="Doutorado">Doutorado</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>
                  
                  <div>
                    <Label>Estado Civil *</Label>
                    <Select
                      value={formData.estado_civil}
                      onValueChange={(value) => setFormData({ ...formData, estado_civil: value })}
                      required
                    >
                      <SelectTrigger>
                        <SelectValue placeholder="Selecione" />
                      </SelectTrigger>
                      <SelectContent position="popper">
                        <SelectItem value="Solteiro(a)">Solteiro(a)</SelectItem>
                        <SelectItem value="Casado(a)">Casado(a)</SelectItem>
                        <SelectItem value="Separado(a)">Separado(a)</SelectItem>
                        <SelectItem value="Divorciado(a)">Divorciado(a)</SelectItem>
                        <SelectItem value="Viúvo(a)">Viúvo(a)</SelectItem>
                        <SelectItem value="União Estável">União Estável</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>
                </div>
              </div>

              {/* QUEIXA COGNITIVA - COLAPSÁVEL */}
              <div className="border rounded-lg">
                <button
                  type="button"
                  onClick={() => toggleSection('queixa')}
                  className="w-full flex items-center justify-between p-4 hover:bg-gray-50"
                >
                  <h3 className="text-lg font-semibold text-emerald-700">
                    Queixa Cognitiva
                  </h3>
                  {collapsedSections.queixa ? <ChevronDown /> : <ChevronUp />}
                </button>
                
                {!collapsedSections.queixa && (
                  <div className="p-4 space-y-4 border-t">
                    <div>
                      <Label>Motivo da Busca</Label>
                      <Textarea
                        value={formData.motivo_busca}
                        onChange={(e) => setFormData({ ...formData, motivo_busca: e.target.value })}
                        rows={3}
                      />
                    </div>
                    
                    <div className="grid grid-cols-2 gap-4">
                      <div>
                        <Label>Duração dos Sintomas</Label>
                        <Input
                          value={formData.duracao_sintomas}
                          onChange={(e) => setFormData({ ...formData, duracao_sintomas: e.target.value })}
                          placeholder="Ex: Há 3 meses"
                        />
                      </div>
                      
                      <div>
                        <Label>Frequência dos Sintomas</Label>
                        <Input
                          value={formData.frequencia_sintomas}
                          onChange={(e) => setFormData({ ...formData, frequencia_sintomas: e.target.value })}
                          placeholder="Ex: Diariamente"
                        />
                      </div>
                    </div>
                    
                    <div>
                      <Label>Tentativas Anteriores de Solução</Label>
                      <Textarea
                        value={formData.tentativas_anteriores}
                        onChange={(e) => setFormData({ ...formData, tentativas_anteriores: e.target.value })}
                        rows={2}
                      />
                    </div>
                    
                    <div>
                      <Label>Expectativas com a Terapia</Label>
                      <Textarea
                        value={formData.expectativas_terapia}
                        onChange={(e) => setFormData({ ...formData, expectativas_terapia: e.target.value })}
                        rows={2}
                      />
                    </div>
                  </div>
                )}
              </div>

              {/* HISTÓRICO DE SAÚDE - COLAPSÁVEL */}
              <div className="border rounded-lg">
                <button
                  type="button"
                  onClick={() => toggleSection('saude')}
                  className="w-full flex items-center justify-between p-4 hover:bg-gray-50"
                >
                  <h3 className="text-lg font-semibold text-emerald-700">
                    Histórico de Saúde
                  </h3>
                  {collapsedSections.saude ? <ChevronDown /> : <ChevronUp />}
                </button>
                
                {!collapsedSections.saude && (
                  <div className="p-4 space-y-4 border-t">
                    <div>
                      <Label>Doenças Físicas Atuais/Prévias</Label>
                      <Textarea
                        value={formData.doencas_fisicas}
                        onChange={(e) => setFormData({ ...formData, doencas_fisicas: e.target.value })}
                        rows={2}
                      />
                    </div>
                    
                    <div>
                      <Label>Uso de Medicamentos Atuais</Label>
                      <Textarea
                        value={formData.medicamentos_atuais}
                        onChange={(e) => setFormData({ ...formData, medicamentos_atuais: e.target.value })}
                        rows={2}
                        placeholder="Nome, dose e indicação"
                      />
                    </div>
                    
                    <div>
                      <Label>Alergias Conhecidas</Label>
                      <Textarea
                        value={formData.alergias}
                        onChange={(e) => setFormData({ ...formData, alergias: e.target.value })}
                        rows={2}
                      />
                    </div>
                  </div>
                )}
              </div>

              {/* HISTÓRICO PESSOAL E SOCIAL - COLAPSÁVEL */}
              <div className="border rounded-lg">
                <button
                  type="button"
                  onClick={() => toggleSection('pessoal')}
                  className="w-full flex items-center justify-between p-4 hover:bg-gray-50"
                >
                  <h3 className="text-lg font-semibold text-emerald-700">
                    Histórico Pessoal e Social
                  </h3>
                  {collapsedSections.pessoal ? <ChevronDown /> : <ChevronUp />}
                </button>
                
                {!collapsedSections.pessoal && (
                  <div className="p-4 space-y-4 border-t">
                    <div>
                      <Label>Com quem reside atualmente?</Label>
                      <Textarea
                        value={formData.com_quem_reside}
                        onChange={(e) => setFormData({ ...formData, com_quem_reside: e.target.value })}
                        rows={2}
                      />
                    </div>
                    
                    <div>
                      <Label>Dinâmica Familiar Atual</Label>
                      <Textarea
                        value={formData.dinamica_familiar}
                        onChange={(e) => setFormData({ ...formData, dinamica_familiar: e.target.value })}
                        rows={2}
                      />
                    </div>
                    
                  </div>
                )}
              </div>

              {/* HÁBITOS E ESTILO DE VIDA - COLAPSÁVEL */}
              <div className="border rounded-lg">
                <button
                  type="button"
                  onClick={() => toggleSection('habitos')}
                  className="w-full flex items-center justify-between p-4 hover:bg-gray-50"
                >
                  <h3 className="text-lg font-semibold text-emerald-700">
                    Hábitos e Estilo de Vida
                  </h3>
                  {collapsedSections.habitos ? <ChevronDown /> : <ChevronUp />}
                </button>
                
                {!collapsedSections.habitos && (
                  <div className="p-4 space-y-4 border-t">
                    <div className="grid grid-cols-2 gap-4">
                      <div>
                        <Label>Qualidade do Sono</Label>
                        <Select
                          value={formData.qualidade_sono}
                          onValueChange={(value) => setFormData({ ...formData, qualidade_sono: value })}
                        >
                          <SelectTrigger>
                            <SelectValue placeholder="Selecione" />
                          </SelectTrigger>
                          <SelectContent position="popper">
                            <SelectItem value="Ótima">Ótima</SelectItem>
                            <SelectItem value="Boa">Boa</SelectItem>
                            <SelectItem value="Regular">Regular</SelectItem>
                            <SelectItem value="Ruim">Ruim</SelectItem>
                            <SelectItem value="Péssima">Péssima</SelectItem>
                          </SelectContent>
                        </Select>
                      </div>
                      
                      <div>
                        <Label>Alimentação</Label>
                        <Select
                          value={formData.alimentacao}
                          onValueChange={(value) => setFormData({ ...formData, alimentacao: value })}
                        >
                          <SelectTrigger>
                            <SelectValue placeholder="Selecione" />
                          </SelectTrigger>
                          <SelectContent position="popper">
                            <SelectItem value="Balanceada">Balanceada</SelectItem>
                            <SelectItem value="Regular">Regular</SelectItem>
                            <SelectItem value="Irregular">Irregular</SelectItem>
                            <SelectItem value="Restritiva">Restritiva</SelectItem>
                          </SelectContent>
                        </Select>
                      </div>
                    </div>
                    
                    <div>
                      <Label>Detalhes do Sono</Label>
                      <Textarea
                        value={formData.detalhes_sono}
                        onChange={(e) => setFormData({ ...formData, detalhes_sono: e.target.value })}
                        rows={2}
                      />
                    </div>
                    
                    <div>
                      <Label>Lazer e Hobbies</Label>
                      <Textarea
                        value={formData.lazer_hobbies}
                        onChange={(e) => setFormData({ ...formData, lazer_hobbies: e.target.value })}
                        rows={2}
                      />
                    </div>
                  </div>
                )}
              </div>

              {/* HISTÓRICO PROFISSIONAL - COLAPSÁVEL */}
              <div className="border rounded-lg">
                <button
                  type="button"
                  onClick={() => toggleSection('profissional')}
                  className="w-full flex items-center justify-between p-4 hover:bg-gray-50"
                >
                  <h3 className="text-lg font-semibold text-emerald-700">
                    Histórico Profissional/Acadêmico
                  </h3>
                  {collapsedSections.profissional ? <ChevronDown /> : <ChevronUp />}
                </button>
                
                {!collapsedSections.profissional && (
                  <div className="p-4 space-y-4 border-t">
                    <div>
                      <Label>Satisfação no Trabalho/Estudo</Label>
                      <Select
                        value={formData.satisfacao_trabalho}
                        onValueChange={(value) => setFormData({ ...formData, satisfacao_trabalho: value })}
                      >
                        <SelectTrigger>
                          <SelectValue placeholder="Selecione" />
                        </SelectTrigger>
                        <SelectContent position="popper">
                          <SelectItem value="Muito Satisfeito">Muito Satisfeito</SelectItem>
                          <SelectItem value="Satisfeito">Satisfeito</SelectItem>
                          <SelectItem value="Indiferente">Indiferente</SelectItem>
                          <SelectItem value="Insatisfeito">Insatisfeito</SelectItem>
                          <SelectItem value="Muito Insatisfeito">Muito Insatisfeito</SelectItem>
                        </SelectContent>
                      </Select>
                    </div>
                    
                  </div>
                )}
              </div>

              {/* OBSERVAÇÕES DO PROFISSIONAL - NÃO COLAPSÁVEL */}
              <div className="space-y-4 border-2 border-red-200 rounded-lg p-4">
                <h3 className="text-lg font-semibold text-red-700 border-b pb-2">
                  Observações do Profissional *
                </h3>
                
                <div>
                  <Label>Impressões Iniciais e Hipóteses *</Label>
                  <Textarea
                    value={formData.impressoes_iniciais}
                    onChange={(e) => setFormData({ ...formData, impressoes_iniciais: e.target.value })}
                    rows={3}
                    required
                  />
                </div>
                
                <div>
                  <Label>Plano de Acompanhamento Inicial *</Label>
                  <Textarea
                    value={formData.plano_acompanhamento}
                    onChange={(e) => setFormData({ ...formData, plano_acompanhamento: e.target.value })}
                    rows={3}
                    required
                  />
                </div>
                
                {/* Observações Adicionais */}
                <div className="border-t pt-4">
                  <Label className="text-base font-semibold">+ Observações Adicionais</Label>
                  
                  {formData.observacoes_adicionais && formData.observacoes_adicionais.length > 0 && (
                    <div className="space-y-2 mt-3">
                      {formData.observacoes_adicionais.map((obs) => (
                        <div key={obs.id} className="p-3 bg-blue-50 rounded border border-blue-200">
                          <p className="text-sm text-gray-800">{obs.texto}</p>
                          <p className="text-xs text-gray-500 mt-1">
                            {new Date(obs.data).toLocaleString('pt-BR')}
                          </p>
                        </div>
                      ))}
                    </div>
                  )}
                  
                  <div className="flex gap-2 mt-3">
                    <Textarea
                      value={novaObservacao}
                      onChange={(e) => setNovaObservacao(e.target.value)}
                      placeholder="Digite uma nova observação..."
                      rows={2}
                      className="flex-1"
                    />
                    <Button
                      type="button"
                      onClick={handleAddObservacao}
                      variant="outline"
                      disabled={!novaObservacao.trim()}
                    >
                      <Plus className="w-4 h-4" />
                    </Button>
                  </div>
                </div>
                
                {/* Documentos */}
                <div className="border-t pt-4">
                  <Label className="text-base font-semibold">Anexar Documentos (Imagens/PDF)</Label>
                  
                  {formData.documentos && formData.documentos.length > 0 && (
                    <div className="space-y-2 mt-3">
                      {formData.documentos.map((doc, index) => (
                        <div key={index} className="flex items-center gap-2 p-2 bg-gray-100 rounded">
                          <File className="w-4 h-4" />
                          <a 
                            href={`${BACKEND_URL}${doc}`} 
                            target="_blank" 
                            rel="noopener noreferrer"
                            className="text-sm text-blue-600 hover:underline flex-1"
                          >
                            {doc.split('/').pop()}
                          </a>
                        </div>
                      ))}
                    </div>
                  )}
                  
                  <Input
                    type="file"
                    accept="image/*,.pdf"
                    onChange={handleUploadDoc}
                    disabled={uploadingDoc || !editingAnamnese}
                    className="mt-3"
                  />
                  {!editingAnamnese && (
                    <p className="text-xs text-gray-500 mt-1">
                      Salve a anamnese primeiro para anexar documentos
                    </p>
                  )}
                </div>
              </div>

              <Button
                type="submit"
                className="w-full bg-emerald-700 hover:bg-emerald-800 text-white"
              >
                {editingAnamnese ? 'Atualizar' : 'Criar'} Anamnese
              </Button>
            </form>
          </DialogContent>
        </Dialog>
      </div>

      {/* Search */}
      <div className="relative">
        <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400 w-5 h-5" />
        <Input
          placeholder="Buscar por nome ou email..."
          value={searchTerm}
          onChange={(e) => setSearchTerm(e.target.value)}
          className="pl-10"
        />
      </div>

      {/* List */}
      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
        {filteredAnamneses.map((anamnese) => (
          <Card key={anamnese.id} className="border-0 shadow-lg hover:shadow-xl transition-shadow">
            <CardContent className="p-6">
              <div className="flex items-start justify-between">
                <div className="flex items-center gap-3">
                  <div className="w-12 h-12 rounded-full bg-emerald-100 flex items-center justify-center">
                    <UserIcon className="w-6 h-6 text-emerald-700" />
                  </div>
                  <div>
                    <h3 className="font-semibold text-gray-900">{anamnese.nome_completo}</h3>
                    <p className="text-sm text-gray-600">{anamnese.email}</p>
                    <p className="text-xs text-gray-500">{anamnese.telefone_principal}</p>
                    {anamnese.nivel_suporte && (
                      <span className="text-xs bg-purple-100 text-purple-700 px-2 py-1 rounded mt-1 inline-block">
                        {anamnese.nivel_suporte}
                      </span>
                    )}
                  </div>
                </div>
                <div className="flex gap-2">
                  <Button
                    variant="ghost"
                    size="icon"
                    onClick={() => openEditDialog(anamnese)}
                  >
                    <Pencil className="w-4 h-4" />
                  </Button>
                  <Button
                    variant="ghost"
                    size="icon"
                    onClick={() => handleDelete(anamnese.id)}
                    className="text-red-600 hover:text-red-700 hover:bg-red-50"
                  >
                    <Trash2 className="w-4 h-4" />
                  </Button>
                </div>
              </div>
            </CardContent>
          </Card>
        ))}
      </div>

      {filteredAnamneses.length === 0 && (
        <Card>
          <CardContent className="p-12 text-center">
            <p className="text-gray-500">Nenhuma anamnese encontrada</p>
          </CardContent>
        </Card>
      )}
    </div>
  );
};

export default Evolucao;
